import { useMemo, useState } from "react";

import { Box, Button, Space } from "@mantine/core";
import { BottomModalSection } from "~/../../base-components/lib/modal/ModalSection";

import {
  CollectionNft,
  CollectionStandardType,
  Condition,
  ConditionType,
  ContractNft,
  NetworkType,
  NftStandardType,
} from "apollo/graphql.generated";
import {
  useCollectionDetailsQuery,
  useContractDetailsQuery,
} from "~/features/gates/queries/gates.graphql.generated";

import ContractCollectionAttributeGrid, {
  TokenGridAttribute,
} from "../../grids/ContractCollectionAttributeGrid";
import { ContractCollectionTokenGrid } from "../../grids/ContractCollectionTokenGrid";
import OpenSeaCollectionTokenGrid from "../../grids/OpenSeaCollectionTokenGrid";
import { SimpleInfiniteListItem } from "../../grids/SimpleInfiniteGrid";
import { CollectionContractInputSection } from "../collection/CollectionContractInputSection";
import CollectionDetailsSection from "../sections/CollectionDetailsSection";
import NetworkSelectionSection from "../sections/NetworkSelectionSection";

export type TokenDetails = {
  id: string;
  name: string;
  contract?: string | null;
  address?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  standard?: NftStandardType | null;
};

const supportedNetworks = [
  NetworkType.ETHEREUM,
  NetworkType.POLYGON,
  NetworkType.BASE,
  NetworkType.OPTIMISM,
  NetworkType.ARBITRUM,
  NetworkType.ZORA,
];

export type SelectedNftContractCondition = {
  network?: NetworkType;
  contract?: string;
  openSeaUrl?: string;
  token?: string;
  condition?: Condition;
};

/**
 * If network is set, then the `data.address` field contains
 * the contract address 0x....
 * If the network is undefined, the `data.address` contains opensea
 * collection name. (if the data.address is not undefined)
 */
export default function ByContractOrUrlTab(props: {
  initialCondition?: Condition;
  onSelected: (item?: SelectedNftContractCondition) => void;
  mode?: "COLLECTION" | "TOKEN" | "ATTRIBUTE";
}) {
  const { initialCondition, mode = "COLLECTION", onSelected } = props;
  const [initialTokenId, setInitialTokenId] = useState<string | undefined>(
    initialCondition?.data?.tokenId ?? undefined
  );

  const [initialAttribute, setInitialAttribute] = useState<string | undefined>(
    initialCondition?.data?.attributes?.at(0)?.value.at(0) ?? undefined
  );

  const [network, setNetwork] = useState<NetworkType | undefined>(
    initialCondition?.data?.network ?? undefined
  );
  const [contract, setContract] = useState<string | undefined>(
    (initialCondition?.data?.network && initialCondition?.data?.address) ??
      undefined
  );
  // Url only contains the collection name, not the whole url. eg: dicklebutts
  const [openSeaUrl, setOpenSeaUrl] = useState<string | undefined>(
    (initialCondition?.data?.network == null &&
      initialCondition?.data?.address) ||
      undefined
  );

  const [selectedOpenSeaToken, setSelectedOpenSeaToken] =
    useState<SimpleInfiniteListItem<CollectionNft>>();

  const [selectedContractToken, setSelectedContractToken] =
    useState<SimpleInfiniteListItem<ContractNft>>();

  const [selectedAttribute, setSelectedAttribute] =
    useState<TokenGridAttribute>();

  const onEdit = () => {
    setSelectedOpenSeaToken(undefined);
    setSelectedContractToken(undefined);
    setSelectedAttribute(undefined);
    setNetwork(undefined);
    setContract(undefined);
    setOpenSeaUrl(undefined);
    setInitialTokenId(undefined);
    setInitialAttribute(undefined);
  };

  // OPENSEA =======================

  const {
    data: openSeaCollection,
    loading: loadingCollection,
    error: openSeaErrors,
  } = useCollectionDetailsQuery({
    variables: { slug: openSeaUrl! },
    skip: !openSeaUrl,
  });

  // CONTRACT =======================

  const { data: contractCollection, loading: loadingContract } =
    useContractDetailsQuery({
      variables: { network: network!, address: contract! },
      skip: !(contract && network),
    });

  // LOGIC =======================

  const handleOnSelectCollection = () => {
    if (mode !== "COLLECTION") return;

    let condition: Condition | undefined;

    if (network && contract && contractCollection) {
      condition = {
        type: ConditionType.NFT,
        amount: initialCondition?.amount ?? 1,
        data: {
          network: network,
          address: contract,
          name: contractCollection.contractDetails.name ?? "",
          collectionName: contractCollection.contractDetails.name ?? "",
          imageUrl: contractCollection.contractDetails.imageUrl,
          standard: contractCollection.contractDetails.standard,
        },
      };
    } else if (openSeaUrl && openSeaCollection) {
      condition = {
        type: ConditionType.COLLECTION,
        amount: initialCondition?.amount ?? 1,
        data: {
          address: openSeaCollection.collectionDetails.slug,
          name: openSeaCollection.collectionDetails.name,
          collectionName: openSeaCollection.collectionDetails.name ?? "",
          imageUrl: openSeaCollection.collectionDetails.imageUrl,
          standard: CollectionStandardType.OPENSEA,
        },
      };
    }

    onSelected({
      network,
      contract,
      openSeaUrl,
      condition,
    });
  };

  const handleOnSelectToken = () => {
    if (mode !== "TOKEN") return;
    let condition: Condition | undefined;

    if (network && contract && contractCollection) {
      if (selectedContractToken == null) return;

      condition = {
        type: ConditionType.NFT,
        amount: initialCondition?.amount ?? 1,
        data: {
          network: network,
          address: contract,
          name: selectedContractToken.data?.name ?? "",
          collectionName: contractCollection.contractDetails.name ?? "",
          imageUrl:
            selectedContractToken.data?.imageUrl ??
            contractCollection.contractDetails.imageUrl ??
            "",
          videoUrl: "", // Todo (jj) this exists? selectedContractToken.data?.videoUrl ?? "",
          tokenId: selectedContractToken.data?.id,
          standard:
            selectedContractToken.data?.standard ??
            contractCollection.contractDetails.standard ??
            "",
        },
      };
      onSelected({
        network,
        contract,
        openSeaUrl,
        token: selectedContractToken.data?.id,
        condition,
      });
      return;
    } else if (openSeaUrl && openSeaCollection) {
      if (selectedOpenSeaToken == null) return;
      condition = {
        type: ConditionType.COLLECTION,
        amount: initialCondition?.amount ?? 1,
        data: {
          address: openSeaCollection.collectionDetails.slug,
          name:
            selectedOpenSeaToken.data?.name ??
            openSeaCollection.collectionDetails.name,
          collectionName: openSeaCollection.collectionDetails.name ?? "",
          imageUrl:
            selectedOpenSeaToken.data?.imageUrl ??
            openSeaCollection.collectionDetails.imageUrl,
          tokenId: selectedOpenSeaToken.data?.id,
          standard: CollectionStandardType.OPENSEA,
        },
      };
      onSelected({
        network,
        contract,
        openSeaUrl,
        token: selectedOpenSeaToken.data?.id,
        condition,
      });
      return;
    }
  };

  const handleOnSelectAttribute = () => {
    if (mode !== "ATTRIBUTE" || selectedAttribute == null) return;

    let condition: Condition | undefined;

    if (network && contract && contractCollection) {
      condition = {
        type: ConditionType.ATTRIBUTE,
        amount: initialCondition?.amount ?? 1,
        data: {
          network,
          address: contract,
          name: contractCollection.contractDetails.name || "",
          imageUrl: contractCollection.contractDetails.imageUrl || "",
          standard: contractCollection.contractDetails.standard || "",
          attributes: [
            {
              key: selectedAttribute.traitType,
              value: [selectedAttribute.traitValue],
            },
          ],
        },
      };

      onSelected({
        network,
        contract,
        openSeaUrl,
        condition,
      });
      return;
    } else if (openSeaUrl && openSeaCollection) {
      condition = {
        type: ConditionType.ATTRIBUTE,
        amount: initialCondition?.amount ?? 1,
        data: {
          network,
          address: openSeaCollection.collectionDetails.slug,
          name: openSeaCollection.collectionDetails.name,
          imageUrl: openSeaCollection.collectionDetails.imageUrl,
          standard: "OpenSea",
          attributes: [
            {
              key: selectedAttribute.traitType,
              value: [selectedAttribute.traitValue],
            },
          ],
        },
      };

      onSelected({
        network,
        contract,
        openSeaUrl,
        condition,
      });
      return;
    }
  };

  const isContinueDisabled = useMemo(() => {
    if (mode === "COLLECTION") {
      return (
        openSeaCollection?.collectionDetails?.standard == null &&
        contractCollection?.contractDetails?.standard == null
      );
    }
    if (mode === "TOKEN") {
      return (
        (selectedOpenSeaToken == null && selectedContractToken == null) ||
        (openSeaCollection?.collectionDetails?.standard != null &&
          selectedOpenSeaToken == null) ||
        (contractCollection?.contractDetails?.standard != null &&
          selectedContractToken == null)
      );
    }
    if (mode === "ATTRIBUTE") {
      return (
        selectedAttribute == null ||
        (openSeaCollection?.collectionDetails?.standard != null &&
          selectedAttribute == null) ||
        (contractCollection?.contractDetails?.standard != null &&
          selectedAttribute == null)
      );
    }
  }, [
    mode,
    selectedAttribute,
    selectedContractToken,
    selectedOpenSeaToken,
    openSeaCollection,
    contractCollection,
  ]);

  const isCollectionSelected = useMemo(() => {
    return !(
      openSeaCollection?.collectionDetails?.standard == null &&
      contractCollection?.contractDetails?.standard == null
    );
  }, [openSeaCollection, contractCollection]);

  const showDetailsComponent = useMemo(() => {
    return (
      openSeaCollection?.collectionDetails?.standard != null ||
      contractCollection?.contractDetails?.standard != null
    );
  }, [
    openSeaCollection?.collectionDetails?.standard,
    contractCollection?.contractDetails?.standard,
  ]);

  return (
    <Box>
      <Box mih={300}>
        <CollectionContractInputSection
          openSeaCollection={openSeaCollection}
          openSeaErrors={openSeaErrors}
          contractCollection={contractCollection}
          onEnterUrl={setOpenSeaUrl}
          onEnterContract={setContract}
          loading={loadingCollection || loadingContract}
          setNetwork={setNetwork}
          placeholderText={"Contract address or OpenSea collection URL"}
        />

        {openSeaUrl == null && (
          <NetworkSelectionSection
            contract={contract}
            network={network}
            availableNetworks={supportedNetworks}
            onNetworkSelection={setNetwork}
            loading={loadingContract}
          />
        )}

        {showDetailsComponent && (
          <>
            <CollectionDetailsSection
              network={network}
              collection={openSeaCollection || contractCollection}
              onClear={onEdit}
            />

            {mode === "TOKEN" && (
              <>
                {openSeaCollection?.collectionDetails?.standard != null && (
                  <>
                    <Space h={"md"} />

                    <OpenSeaCollectionTokenGrid
                      initialTokenId={initialTokenId}
                      openSeaCollection={openSeaCollection.collectionDetails}
                      selectedToken={selectedOpenSeaToken}
                      onSelectToken={setSelectedOpenSeaToken}
                    />
                  </>
                )}

                {contractCollection?.contractDetails?.standard != null &&
                  network && (
                    <>
                      <Space h={"md"} />

                      <ContractCollectionTokenGrid
                        initialTokenId={initialTokenId}
                        contractCollection={contractCollection.contractDetails}
                        network={network}
                        selectedToken={selectedContractToken}
                        onSelectToken={setSelectedContractToken}
                      />
                    </>
                  )}
              </>
            )}

            {mode === "ATTRIBUTE" && (
              <>
                {openSeaCollection?.collectionDetails?.standard != null && (
                  <>
                    <Space h={"xl"} />
                    <ContractCollectionAttributeGrid
                      initialAttribute={initialAttribute}
                      openSeaUrl={openSeaCollection.collectionDetails.slug}
                      selectedAttribute={selectedAttribute}
                      onSelectAttribute={setSelectedAttribute}
                    />
                  </>
                )}

                {contractCollection?.contractDetails?.standard != null &&
                  network && (
                    <>
                      <Space h={"xl"} />
                      <ContractCollectionAttributeGrid
                        initialAttribute={initialAttribute}
                        network={network}
                        contract={contract}
                        selectedAttribute={selectedAttribute}
                        onSelectAttribute={setSelectedAttribute}
                      />
                    </>
                  )}
              </>
            )}
          </>
        )}
      </Box>

      <Space h="sm" />

      <BottomModalSection>
        {mode === "COLLECTION" && (
          <Button
            disabled={isContinueDisabled}
            size="xl"
            fullWidth
            onClick={handleOnSelectCollection}
          >
            Select collection
          </Button>
        )}

        {mode === "TOKEN" && (
          <>
            {isCollectionSelected ? (
              <Button
                disabled={isContinueDisabled}
                size="xl"
                fullWidth
                onClick={handleOnSelectToken}
              >
                {"Select token" +
                  ((selectedContractToken?.data?.id &&
                    ` ( ${selectedContractToken?.data?.id} )`) ||
                    (selectedOpenSeaToken?.data?.id &&
                      ` ( ${selectedOpenSeaToken?.data?.id} )`) ||
                    "")}
              </Button>
            ) : (
              <Button disabled={isContinueDisabled} size="xl" fullWidth>
                Select collection
              </Button>
            )}
          </>
        )}

        {mode === "ATTRIBUTE" && (
          <>
            {isCollectionSelected ? (
              <Button
                disabled={isContinueDisabled}
                size="xl"
                fullWidth
                onClick={handleOnSelectAttribute}
              >
                Select attribute
              </Button>
            ) : (
              <Button disabled={isContinueDisabled} size="xl" fullWidth>
                Select collection
              </Button>
            )}
          </>
        )}
      </BottomModalSection>
    </Box>
  );
}
